import { Payload, PayloadCategory } from "@/types/profile";
import { LucideIcon } from "lucide-react";
import * as Icons from "lucide-react";

// Map payload categories to appropriate icons
const categoryIconMap: Record<PayloadCategory, string> = {
  Security: "Shield",
  Network: "Wifi",
  General: "Settings",
  Accounts: "Users",
  Restrictions: "Ban",
  Media: "Play",
  Applications: "AppWindow",
  Advanced: "Wrench",
  Education: "GraduationCap"
};

// Special payload-specific icons for more granular matching
const payloadIconMap: Record<string, string> = {
  "passcode": "Lock",
  "firewall": "ShieldAlert",
  "wifi": "Wifi",
  "vpn": "ShieldCheck",
  "certificate": "FileKey",
  "encryption": "Key",
  "privacy-preferences": "Eye",
  "login-window": "LogIn",
  "restrictions": "Ban",
  "single-app-mode": "Smartphone",
  "airplay": "Cast",
  "airprint": "Printer",
  "app-install": "Download",
};

/**
 * Determines the best icon for a profile based on its payloads
 * Priority order:
 * 1. Most specific payload icon if available
 * 2. Most common category icon
 * 3. Default "Settings" icon
 */
export function selectProfileIcon(payloads: Payload[]): string {
  if (payloads.length === 0) {
    return "Settings";
  }

  // First, check if any payload has a specific icon mapping
  for (const payload of payloads) {
    if (payloadIconMap[payload.id]) {
      return payloadIconMap[payload.id];
    }
  }

  // Count categories to find the most common one
  const categoryCounts: Record<string, number> = {};
  payloads.forEach(payload => {
    categoryCounts[payload.category] = (categoryCounts[payload.category] || 0) + 1;
  });

  // Find the most common category
  const dominantCategory = Object.entries(categoryCounts)
    .sort(([, a], [, b]) => b - a)[0]?.[0] as PayloadCategory;

  return categoryIconMap[dominantCategory] || "Settings";
}

// ---------------------------------------------------------------------------
// Backend-policy icon inference (used when profiles come from the API and we
// only have raw payload identifiers + a profile name, not rich Payload objects)
// ---------------------------------------------------------------------------

// Priority used to break ties when multiple identifiers match different icons.
// Higher = wins.
const ICON_PRIORITY: Record<string, number> = {
  ShieldCheck: 100,
  Shield: 95,
  ShieldAlert: 94,
  Lock: 90,
  Key: 85,
  FileKey: 80,
  Eye: 78,
  LogIn: 75,
  Fingerprint: 73,
  Wifi: 70,
  Ban: 65,
  Mail: 62,
  Calendar: 61,
  Contact: 60,
  Users: 59,
  AppWindow: 55,
  Globe: 50,
  LayoutGrid: 48,
  GraduationCap: 45,
  Cast: 40,
  Printer: 38,
  Bell: 36,
  Play: 35,
  RefreshCw: 34,
  Server: 33,
  Filter: 32,
  Bluetooth: 30,
  Signal: 29,
  Clock: 28,
  Battery: 26,
  HardDrive: 24,
  Activity: 22,
  Wrench: 20,
  Settings: 0,
};

// Apple payload identifier (or substring) → icon name. Order matters: more
// specific entries should appear first; resolution does substring matching.
const IDENTIFIER_ICON_RULES: Array<{ match: RegExp; icon: string }> = [
  // Security
  { match: /passwordpolicy|passcode/i, icon: "Lock" },
  { match: /firewall/i, icon: "ShieldAlert" },
  { match: /filevault|encryption/i, icon: "Key" },
  { match: /TCC\.configuration-profile-policy|privacy-preferences|\bpppc\b/i, icon: "Eye" },
  { match: /loginwindow/i, icon: "LogIn" },
  { match: /vpn/i, icon: "ShieldCheck" },
  // Identity / identification — Apple's "configurationprofile.identification"
  // and SSO/Kerberos extension payloads describe who the user/device is.
  { match: /configurationprofile\.identification|\.identification\b|\bidentity\b|extensiblesso|kerberos/i, icon: "Fingerprint" },
  // Software / system management — listed BEFORE the broad MCX rule so
  // narrower MCX.* sub-payloads (energysaver, etc.) win their specific icon.
  { match: /SoftwareUpdate|softwareupdate/i, icon: "RefreshCw" },
  // Cellular family — listed BEFORE the broad `mdm` rule so that vendor
  // identifiers like `com.apple.mdm.managedCarrier` resolve to Signal rather
  // than Server. Covers:
  //   • Apple cellular payloads: com.apple.cellular[.Private|.Data|.AccessRules|.*]
  //   • Carrier bundles & carrier-profile variants: com.apple.carrierBundle*,
  //     com.apple.carrier.profile, com.apple.carrier_profile, *carrierprofile*
  //   • Managed carrier: com.apple.managedCarrier, *.managed-carrier,
  //     *.managed_carrier, com.apple.mdm.managedCarrier, vendor.managedCarrier.*
  //   • eSIM in any position: com.apple.eSIM, *.eSIM.*, eSIMActivation
  //   • APN settings payload (com.apple.apn-settings) — but NOT bare "apns"
  //     which belongs to Apple Push Notification Service.
  {
    match: /\.cellular(?:Private|Data|AccessRules)?\b|\.cellular\.|\.carrierBundle\b|\.carrier\.|\bcarrier[_-]?profile\b|\bmanaged[_-]?carrier\b|\besim\b|\bcellularaccessrules\b|com\.apple\.apn-settings\b/i,
    icon: "Signal",
  },
  { match: /mdm(?!\w)|deviceManagement|managementOptions|enrollmentProfile|\.enrollment\b/i, icon: "Server" },
  { match: /notifications?(?:settings)?|com\.apple\.notificationsettings/i, icon: "Bell" },
  { match: /contentfilter|webcontentfilter/i, icon: "Filter" },
  { match: /associatedDomains|associated-domains/i, icon: "Globe" },
  { match: /dnsSettings|dnsProxy|\.dns\b/i, icon: "Server" },
  { match: /proxy/i, icon: "Server" },
  { match: /bluetooth/i, icon: "Bluetooth" },
  { match: /energy|energysaver|powermanagement/i, icon: "Battery" },
  { match: /timeserver|\bntp\b|datetime|timezone/i, icon: "Clock" },
  { match: /diskmanagement|\.disk\b|storage/i, icon: "HardDrive" },
  { match: /diagnostics?|SubmitDiagInfo|reporting|telemetry/i, icon: "Activity" },
  // Compliance baselines — broad MCX preferences match comes LAST among
  // system rules so more specific MCX.* payloads above can win first.
  { match: /ManagedClient\.preferences/i, icon: "ShieldCheck" },
  // UI / layout
  { match: /webClip/i, icon: "Globe" },
  { match: /homescreenlayout|home-screen-layout/i, icon: "LayoutGrid" },
  // Networking
  { match: /wifi|airport|\.network\b|\.network\./i, icon: "Wifi" },
  // Certs
  { match: /scep|pkcs1|pkcs12|certificate/i, icon: "FileKey" },
  // Restrictions
  { match: /applicationaccess|restrictions/i, icon: "Ban" },
  // Media / peripherals
  { match: /airplay/i, icon: "Cast" },
  { match: /airprint|printing|\.print\b/i, icon: "Printer" },
  // Accounts (split for granularity)
  { match: /mail\.managed|imap|smtp|pop3/i, icon: "Mail" },
  { match: /\beas\b|exchange/i, icon: "Mail" },
  { match: /caldav|calendar/i, icon: "Calendar" },
  { match: /carddav|contacts/i, icon: "Contact" },
  { match: /ldap|googleaccount|subscribedcalendar/i, icon: "Users" },
  { match: /\.account\b|\.account\./i, icon: "Users" },
  // Misc
  { match: /font/i, icon: "Wrench" },
];

// Profile-name keyword → icon. Lets compliance/CIS/mSCP profiles surface a
// shield even when their identifiers are generic (e.g. MCX).
const NAME_ICON_RULES: Array<{ match: RegExp; icon: string }> = [
  { match: /\b(cis|mscp|benchmark|compliance|hardening|baseline)\b/i, icon: "ShieldCheck" },
  { match: /\b(security|secure)\b/i, icon: "Shield" },
  { match: /\b(passcode|password)\b/i, icon: "Lock" },
  { match: /\b(filevault|encryption|encrypt)\b/i, icon: "Key" },
  { match: /\b(privacy|pppc|tcc)\b/i, icon: "Eye" },
  { match: /\b(vpn)\b/i, icon: "ShieldCheck" },
  // Cellular keywords are listed BEFORE the broad "network" rule so that
  // names like "5G Network" or "LTE Network" resolve to Signal rather than
  // Wifi. Bare "apn" is intentionally omitted (collides with APNs push) —
  // the multi-word "APN Settings"/"APN Config" form is required instead.
  // Cellular keywords. We treat `_` and `-` as word separators (the standard
  // `\b` does not break on `_`), so names like `5g_network` and `LTE-Network`
  // still resolve to Signal. The APN form accepts space/`-`/`_` between
  // "APN" and "settings"/"config"/"profile".
  { match: /(?:^|[^a-z0-9])(?:cellular|carrier|esim|lte|5g|4g)(?:[^a-z0-9]|$)|(?:^|[^a-z0-9])apn[\s_-]*(?:settings?|config(?:uration)?s?|profile)(?:[^a-z0-9]|$)/i, icon: "Signal" },
  { match: /\b(wifi|wi-fi|network)\b/i, icon: "Wifi" },
  { match: /\b(certificate|cert|scep)\b/i, icon: "FileKey" },
  { match: /\b(restriction|restrict)\b/i, icon: "Ban" },
  { match: /\b(education|school|classroom)\b/i, icon: "GraduationCap" },
  { match: /\b(home\s*screen|layout|dock)\b/i, icon: "LayoutGrid" },
  { match: /\b(webclip|web\s*clip)\b/i, icon: "Globe" },
  { match: /\b(login|signin|sign-in)\b/i, icon: "LogIn" },
  { match: /\b(identity|identification|identify|sso|kerberos)\b/i, icon: "Fingerprint" },
  // Accounts / mail / calendar / contacts
  { match: /\b(mail|imap|smtp|pop3|exchange|eas)\b/i, icon: "Mail" },
  { match: /\b(calendar|caldav)\b/i, icon: "Calendar" },
  { match: /\b(contacts?|carddav)\b/i, icon: "Contact" },
  { match: /\b(account|accounts|ldap|directory)\b/i, icon: "Users" },
  // System
  { match: /\b(software\s*update|patching|patch|upgrade)\b/i, icon: "RefreshCw" },
  { match: /\b(mdm|enrollment|management)\b/i, icon: "Server" },
  { match: /\b(notification|notifications|alert)\b/i, icon: "Bell" },
  { match: /\b(content\s*filter|web\s*filter|filter)\b/i, icon: "Filter" },
  { match: /\b(dns|proxy)\b/i, icon: "Server" },
  { match: /\b(bluetooth)\b/i, icon: "Bluetooth" },
  { match: /\b(energy|power|battery)\b/i, icon: "Battery" },
  { match: /\b(time|ntp|timezone|clock)\b/i, icon: "Clock" },
  { match: /\b(disk|storage|volume)\b/i, icon: "HardDrive" },
  { match: /\b(diagnostic|telemetry|reporting)\b/i, icon: "Activity" },
  { match: /\b(printer|printing|airprint)\b/i, icon: "Printer" },
  { match: /\b(airplay|cast)\b/i, icon: "Cast" },
  { match: /\b(application|apps?)\b/i, icon: "AppWindow" },
];

function iconFromIdentifier(identifier: string): string | null {
  if (!identifier) return null;
  for (const rule of IDENTIFIER_ICON_RULES) {
    if (rule.match.test(identifier)) return rule.icon;
  }
  return null;
}

function iconFromName(name: string): string | null {
  if (!name) return null;
  for (const rule of NAME_ICON_RULES) {
    if (rule.match.test(name)) return rule.icon;
  }
  return null;
}

/**
 * Infer the best icon for a profile loaded from the backend, where we only
 * have raw payload identifiers (e.g. "com.apple.mobiledevice.passwordpolicy")
 * and the profile name. Falls back to "Settings" so existing behavior is
 * preserved when nothing matches.
 */
export function inferProfileIconFromPolicy(
  name: string | undefined | null,
  identifiers: Array<string | undefined | null>
): string {
  // 1. Tally icons across all identifiers.
  const counts: Record<string, number> = {};
  for (const id of identifiers) {
    const icon = iconFromIdentifier(id || "");
    if (icon) counts[icon] = (counts[icon] || 0) + 1;
  }

  // 2. Add a name-based hint as an extra vote (helps mSCP/CIS profiles whose
  //    identifiers are MCX-generic but whose name carries the real intent).
  const nameIcon = iconFromName(name || "");
  if (nameIcon) counts[nameIcon] = (counts[nameIcon] || 0) + 1;

  const entries = Object.entries(counts);
  if (entries.length === 0) return "Settings";

  // 3. Pick the highest-priority icon. Security/identity signals should win
  //    over generic utilities even when the utility appears more often
  //    (e.g. a profile with 3 cellular payloads + 1 passcode payload should
  //    surface the Lock, since security intent dominates the user's mental
  //    model). Use occurrence count only as a tiebreaker between icons of
  //    equal priority.
  entries.sort((a, b) => {
    const pDiff = (ICON_PRIORITY[b[0]] ?? 0) - (ICON_PRIORITY[a[0]] ?? 0);
    if (pDiff !== 0) return pDiff;
    return b[1] - a[1];
  });
  return entries[0][0];
}

/**
 * Get the Lucide icon component from icon name string
 */
export function getIconComponent(iconName: string): LucideIcon {
  const IconComponent = (Icons as any)[iconName];
  return IconComponent || Icons.Settings;
}

/**
 * Get a human-readable description of what the icon represents
 */
export function getIconDescription(iconName: string): string {
  const descriptions: Record<string, string> = {
    Shield: "Security configuration",
    Lock: "Access control",
    ShieldAlert: "Security protection",
    ShieldCheck: "VPN & secure connections",
    Wifi: "Network settings",
    Settings: "General configuration",
    Users: "User accounts",
    Ban: "Restrictions & policies",
    Play: "Media settings",
    AppWindow: "Application management",
    Wrench: "Advanced settings",
    GraduationCap: "Education settings",
    FileKey: "Certificate management",
    Key: "Encryption settings",
    Eye: "Privacy settings",
    LogIn: "Login & authentication",
    Smartphone: "Device restrictions",
    Cast: "Media streaming",
    Printer: "Printing settings",
    Download: "App deployment",
    Fingerprint: "Identity & identification",
    Mail: "Mail accounts",
    Calendar: "Calendar accounts",
    Contact: "Contacts accounts",
    RefreshCw: "Software updates",
    Server: "MDM & server settings",
    Bell: "Notifications",
    Filter: "Content filtering",
    Globe: "Web & domains",
    LayoutGrid: "Home screen layout",
    Bluetooth: "Bluetooth settings",
    Battery: "Power & energy",
    Clock: "Time & date",
    HardDrive: "Disk & storage",
    Activity: "Diagnostics & telemetry",
    Signal: "Cellular settings"
  };
  return descriptions[iconName] || "Configuration profile";
}
